/**
 * RevenueChart — Today / This Week / This Month revenue snapshot.
 *
 * #221 Wave 1: converted from inert Tailwind utilities (admin-app has
 * no Tailwind build) to the ferro-* design system. The widget now
 * shares the same card chrome as FacilityStatus & co.
 * (.ferro-dashboard-widget), trend arrows carry explicit width/height
 * attributes (the old `w-3 h-3`-only sizing rendered them at the SVG
 * intrinsic 300×150 default — Johnny's "giant arrows" finding), and
 * load failures render friendly copy + Retry instead of raw machine
 * codes.
 */
import { useState, useEffect, useCallback } from 'react';
import type { ClientConfig } from '@ferro/config';
import { adminAnalyticsApi, type RevenueSnapshot } from '../../lib/api.js';

/**
 * Defensive money formatter — server contracts say `today` / `thisWeek`
 * / `thisMonth` are numbers, but real responses (especially stub /
 * partial / 304-cache responses observed in test mocks and in early
 * cold-start retries) can return `undefined`. Falling back to `0` keeps
 * the dashboard rendering instead of crashing into the ErrorBoundary.
 */
function money(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function ChangeIndicator({ value }: { value: number }): JSX.Element {
  if (value === 0) {
    return <span className="ferro-stat__delta ferro-stat__delta--flat">—</span>;
  }
  const isUp = value > 0;
  return (
    <span
      className={`ferro-stat__delta ${
        isUp ? 'ferro-stat__delta--up' : 'ferro-stat__delta--down'
      }`}
    >
      <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
          clipRule="evenodd"
        />
      </svg>
      {Math.abs(value)}%
      <span className="sr-only">{isUp ? 'up' : 'down'} from previous period</span>
    </span>
  );
}

const STAT_LABELS = [
  { key: 'today', changeKey: 'todayChange', label: 'Today' },
  { key: 'thisWeek', changeKey: 'weekChange', label: 'This Week' },
  { key: 'thisMonth', changeKey: 'monthChange', label: 'This Month' },
] as const;

type RevenueChartProps = {
  config: ClientConfig;
};

export function RevenueChart({ config }: RevenueChartProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [data, setData] = useState<RevenueSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRevenue = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const result = await adminAnalyticsApi.getRevenue(apiUrl);
    setIsLoading(false);
    if (result.error) {
      // Friendly copy only — never surface raw machine codes (#221 QA).
      // Breadcrumb keeps the raw cause findable in the console.
      console.error('[RevenueChart] revenue load failed:', result.error);
      setError("Couldn't load revenue right now.");
    } else if (result.data) {
      setData(result.data);
    }
  }, [apiUrl]);

  useEffect(() => {
    void fetchRevenue();
  }, [fetchRevenue]);

  return (
    <article
      className="ferro-dashboard-widget"
      aria-label="Revenue snapshot"
      data-testid="revenue-widget"
    >
      <header className="ferro-dashboard-widget__header">
        <h2 className="ferro-section__subtitle">Revenue</h2>
        {!isLoading ? (
          <button
            type="button"
            onClick={() => void fetchRevenue()}
            className="ferro-dashboard-widget__action"
          >
            Refresh
          </button>
        ) : (
          <div
            className="ferro-dashboard-widget__spinner"
            role="status"
            aria-label="Loading revenue"
          />
        )}
      </header>

      {error && (
        <div
          className="ferro-dashboard-widget__error ferro-dashboard-widget__error--retry"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void fetchRevenue()}
            className="ferro-dashboard-widget__action"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading && !data && (
        <div className="ferro-stat-grid" data-testid="revenue-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ferro-skeleton ferro-skeleton--stat" />
          ))}
        </div>
      )}

      {data && (
        <div className="ferro-stat-grid">
          {STAT_LABELS.map(({ key, changeKey, label }) => (
            <div key={key} className="ferro-stat">
              <span className="ferro-stat__label">{label}</span>
              <span className="ferro-stat__value">${money(data[key])}</span>
              <ChangeIndicator value={data[changeKey] ?? 0} />
            </div>
          ))}
        </div>
      )}

      {!data && !error && !isLoading && (
        <p className="ferro-centered-note">No revenue data available</p>
      )}
    </article>
  );
}
